import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { useToast } from '../hooks/use-toast';
import api from '../services/api';
import StudySessionEditor from './StudySessionEditor';
import SessionLessonManager from './SessionLessonManager';

const AdminStudySessions = ({ onBack }) => {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showNewSession, setShowNewSession] = useState(false);
  const [showLessonManager, setShowLessonManager] = useState(null);
  const { toast } = useToast();

  useEffect(() => {
    fetchSessions();
  }, []);

  const fetchSessions = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await api.get('/admin/study-sessions', {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      setSessions(response.data.sessions);
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to fetch study sessions",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSession = async (session_id) => {
    if (!window.confirm(`Are you sure you want to delete this study session? This will not delete the lessons.`)) {
      return;
    }

    try {
      const token = localStorage.getItem('adminToken');
      const trimmedSessionId = (session_id || '').trim();
      await api.delete(`/admin/study-sessions/${trimmedSessionId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      toast({
        title: "Success",
        description: `Study session deleted successfully`
      });
      
      fetchSessions();
    } catch (error) {
      // If 404, the session doesn't exist - treat as success and refresh
      if (error.response?.status === 404) {
        toast({
          title: "Notice",
          description: "Session already deleted or doesn't exist. Refreshing list...",
        });
        fetchSessions();
      } else {
        toast({
          title: "Error",
          description: error.response?.data?.detail || "Failed to delete session",
          variant: "destructive"
        });
      }
    }
  };

  if (selectedSession || showNewSession) {
    return (
      <StudySessionEditor
        session={selectedSession}
        onBack={() => {
          setSelectedSession(null);
          setShowNewSession(false);
          fetchSessions();
        }}
        isNew={showNewSession}
      />
    );
  }

  if (showLessonManager) {
    return (
      <SessionLessonManager
        session={showLessonManager}
        onBack={() => {
          setShowLessonManager(null);
          fetchSessions();
        }}
      />
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Study Sessions</h1>
            <p className="text-gray-600 mt-1">Manage study sessions and their lessons</p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => setShowNewSession(true)}>
              Add New Session
            </Button>
            <Button onClick={onBack} variant="outline">
              Back to Admin
            </Button>
          </div>
        </div>

        <div className="grid gap-4 mb-6 md:grid-cols-3">
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium">Total Sessions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{sessions.length}</div>
            </CardContent>
          </Card>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>All Study Sessions</CardTitle>
            <CardDescription>View and manage study session content</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p>Loading sessions...</p>
            ) : sessions.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500 mb-4">No study sessions yet</p>
                <Button onClick={() => setShowNewSession(true)}>
                  Create Your First Session
                </Button>
              </div>
            ) : (
              <div className="space-y-2">
                {sessions.map((session) => (
                  <div
                    key={session.session_id}
                    className="flex items-center justify-between p-4 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1">
                      <h3 className="font-semibold text-lg">{session.title}</h3>
                      <p className="text-sm text-gray-600 mt-1">{session.description}</p>
                      <p className="text-xs text-gray-500 mt-2">
                        <strong>Reference:</strong> {session.passage_reference} | 
                        <strong> Lessons:</strong> {session.lesson_ids?.length || 0}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        onClick={() => setShowLessonManager(session)}
                        variant="outline"
                        size="sm"
                      >
                        Manage Lessons
                      </Button>
                      <Button
                        onClick={() => setSelectedSession(session)}
                        variant="outline"
                        size="sm"
                      >
                        Edit
                      </Button>
                      <Button
                        onClick={() => handleDeleteSession(session.session_id)}
                        variant="destructive"
                        size="sm"
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminStudySessions;
